<script lang="ts">
import {computed, defineComponent, onMounted, ref, watch} from 'vue';
import MasterLayout from "~/layouts/MasterLayout.vue";
import RegistrationPlate from "~/components/vehicle/RegistrationPlate.vue";
import {useTranslationStore, translation} from '~/plugins/store/translations';
import type {TranslationList} from "~/types/Utils";
import {useProductStore} from "~/plugins/store/product";
import {createDefaultRcaVehicleFormValidation, type RcaVehicleFormValidation} from "~/types/Vehicle";
import type {ProductStatuses} from "~/types/Product";
import {usePageStore} from "~/plugins/store/page";
import {useRcaStore} from "~/plugins/store/rca";
import type {RcaInsuranceCompany} from "~/types/Rca";
import type {FaqQuestion, FaqCategory, Review} from "~/types/Page";
import {useAuthStore} from "~/plugins/store/auth";
import CardAction from "~/components/utils/CardAction.vue";

export default defineComponent({
  setup() {
    useHead({
      title: 'Xiga',
      meta: [
        {
          name: 'description',
          content: 'meta'
        }
      ],
    });

    const pageStore = usePageStore();
    const faqCategories = ref<FaqCategory[] | null>([]);
    const faqQuestions = ref<FaqQuestion[] | null>([]);
    const reviews = ref<Review[] | null>([]);
    const {$toast} = useNuxtApp();
    const translationStore = useTranslationStore();
    const translationList = ref<TranslationList>({});
    const productStore = useProductStore();
    const productStatuses = ref<ProductStatuses>(productStore.productStatuses);
    const faqTab = ref("0");
    const playAnimations = ref(false);
    const authStore = useAuthStore();
    const user = computed(() => authStore.getUser());

    const rcaStore = useRcaStore();
    const rcaInsuranceCompanies = ref<RcaInsuranceCompany[]>([]);

    const registrationPlate = ref('');
    const validationVehicleStatus = ref<RcaVehicleFormValidation>(createDefaultRcaVehicleFormValidation());
    validationVehicleStatus.value.registration_plate = true;

    onMounted(async () => {
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');

      rcaInsuranceCompanies.value = await rcaStore.getAllRcaInsuranceCompanies();

      const fetchedFaqCategories = await pageStore.getFaqCategories();
      faqCategories.value = fetchedFaqCategories?.filter(c => ['home', 'rca', 'travel'].includes(c.code?.toLowerCase())) ?? null;
      faqQuestions.value = await pageStore.getQuestions();

      reviews.value = await pageStore.getReviews(3);

      playAnimations.value = true;
    });


    const handleValidationVehicleStatus = ({field, isValid}: { field: string, isValid: boolean }) => {
      if (field === 'registration_plate') {
        validationVehicleStatus.value[field] = isValid;
      }
    };

    const isValidRegistrationPlate = () => {
      return !!validationVehicleStatus.value.registration_plate;
    };

    // Metoda de filtrare a întrebărilor pe baza categoriei curente
    const filteredQuestions = (categoryId: number) => {
      if (faqQuestions.value) {
        return faqQuestions.value.filter(q => q.category_id === categoryId);
      }
      return [];
    };

    return {
      translationList,
      translation,
      registrationPlate,
      rcaInsuranceCompanies,
      productStatuses,
      handleValidationVehicleStatus,
      isValidRegistrationPlate,
      faqTab,
      faqCategories,
      faqQuestions,
      filteredQuestions,
      reviews,
      playAnimations,
      user
    };
  },
  components: {CardAction, RegistrationPlate, MasterLayout},
  data: () => ({}),
});
</script>

<template>
  <master-layout class="index">
    <v-container class="content-container" style="padding-top: 0;">
      <!-- Main container -->
      <v-container fluid class="component-container-full">
        <v-container fluid class="component-container-1400mw">
          <v-row class="d-flex justify-center align-center">
            <v-col cols="12" sm="12" md="6" lg="6" xl="6">
              <div class="d-flex flex-column h-100">
                <div>
                  <div class="my-auto">
                    <div class="fw-700 fs-24 text-primary">{{
                        translation(translationList.welcome, "Bine ai venit")
                      }}<span v-if="user">, {{ user.full_name }}!</span><span v-else>!</span></div>
                    <div class="fw-700 fs-36 text-primary">
                      {{
                        translation(translationList.you_decide, "Cu ce te putem ajuta azi?")
                      }}
                    </div>
                  </div>
                </div>
                <div class="my-5">
                  <v-row>
                    <v-col cols="12" sm="12" class="pulse animated">
                      <card-action
                          :title="translation(translationList.rca, 'RCA')"
                          :subtitle="translation(translationList.rca_subtitle, 'Descopera ofertele societatilor de asigurare.')"
                          :action="translation(translationList.compare, 'Calculeaza RCA')"
                          redirectToRoute="/rca"/>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" sm="12" class="pulse animated">
                      <card-action
                          :title="translation(translationList.rca, 'Asigurare de calatorie')"
                          :subtitle="translation(translationList.rca_subtitle, 'Descopera ofertele societatilor de asigurare.')"
                          :action="translation(translationList.compare, 'Calculeaza Asigurarea de calatorie')"
                          redirectToRoute="/travel"/>
                    </v-col>
                  </v-row>
                </div>
              </div>
            </v-col>
            <v-col cols="12" sm="6" md="6" lg="6" xl="6" class="visible-on-web text-center">

              <NuxtImg
                  src="/images/hero.png"
                  :class="playAnimations ? 'pulse animated' : ''"
                  style="width: 100%; height: auto; max-width: 100%;"
                  fit="cover"
                  alt="Homepage family"
                  loading="lazy"
              />
            </v-col>
          </v-row>
        </v-container>
      </v-container>

      <!-- For you container   -->
      <section class="diagonal-section py-10">
        <v-container fluid class="component-container-full">
          <v-container class="container">
            <v-container class="component-container-1400mw">
              <div class="my-15">
                <div class="d-flex flex-row align-center justify-center my-5">
                  <div class="fw-700 fs-30 mx-3 color-primary">{{
                      translation(translationList.partner_insurance_companies, "Companii de Asigurare Partenere")
                    }}
                  </div>
                </div>
                <v-row class="d-flex align-center justify-center mt-5">
                  <div class="d-flex flex-wrap align-center justify-center pa-1">
                    <v-col cols="auto" sm="auto" md="auto" class="pa-1"
                           v-for="(insuranceCompany, index) in rcaInsuranceCompanies"
                           :key="index">
                      <v-card height="80" width="130" rounded="lg" flat color="gray-light"
                              class="d-flex align-center justify-center">
                        <NuxtImg :src="'/images/companies/insurance/thumbnails/' + insuranceCompany.code + '.webp'"
                                 :alt="insuranceCompany.name" class="pa-1 w-100 pulse animated"/>
                      </v-card>
                    </v-col>
                  </div>
                </v-row>
              </div>

              <div class="d-flex flex-column align-center justify-center">
                <v-row class="d-flex align-center">
                  <v-col cols="12" sm="12" md="6" lg="4" class="d-flex justify-center">
                    <NuxtImg
                        src="/images/logo.webp"
                        srcset="/images/logo.webp 480w, /images/logo.webp 1024w"
                        sizes="(max-width: 600px) 480px, 1024px"
                        style="object-fit: cover; max-width: 380px;"
                        alt="Logo"
                        loading="lazy"
                    />
                  </v-col>
                  <v-col cols="12" sm="12" md="6" lg="8" class="d-flex flex-column centered-on-mobile px-10">
                    <div class="fw-700 fs-30">
                      {{ translation(translationList.about_us, "XIGA – Inovație și accesibilitate în distribuția de asigurări") }}
                    </div>
                    <div class="fw-500 fs-16">
                      {{
                        translation(translationList.about_us_description, "XIGA transformă modul în care consumatorii și partenerii interacționează cu serviciile de asigurări, oferind soluții moderne, mobile-first și conforme reglementărilor. Prin parteneriate cu comercianți online și locații fizice, XIGA face asigurările accesibile, convenabile și ușor de achiziționat, contribuind la creșterea accesului la servicii financiare pentru consumatori.")
                      }}
                    </div>
                  </v-col>
                </v-row>
                <v-row class="my-15">
                  <v-col cols="12" md="6" lg="4" xl="4">
                    <v-card
                        color="gray-light"
                        class="mx-auto text-center text-primary"
                        max-width="344"
                        variant="flat"
                    >
                      <NuxtImg
                          src="/images/misc/reason-one.png"
                          srcset="/images/misc/reason-one-small.png 110w, /images/misc/reason-one.png 200w"
                          sizes="(max-width: 600px) 480px, 1024px"
                          style="object-fit: cover; max-width: 180px;"
                          alt="Circle 1"
                          :class="playAnimations ? 'pulse animated' : ''"
                          loading="lazy"
                      />

                      <v-card-title class="fw-700">
                        {{ translation(translationList.mobility_title, "Solutii pentru mobilitate") }}
                      </v-card-title>
                      <v-card-text class="fw-500">
                        {{
                          translation(translationList.mobility_desc, "Vrem sa aducem in aceeasi platforma furnizori verificati care sa-ti rezolve problemele ce pot aparea in cursul unei calatorii cu masina, la nivel national si regional.")
                        }}
                      </v-card-text>
                    </v-card>
                  </v-col>
                  <v-col cols="12" md="6" lg="4" xl="4">
                    <v-card
                        color="gray-light"
                        class="mx-auto text-center text-primary"
                        max-width="344"
                        variant="flat"
                    >
                      <NuxtImg
                          src="/images/misc/reason-two.png"
                          srcset="/images/misc/reason-two-small.png 110w, /images/misc/reason-two.png 200w"
                          sizes="(max-width: 600px) 480px, 1024px"
                          style="object-fit: cover; max-width: 180px;"
                          :class="playAnimations ? 'pulse animated' : ''"
                          alt="Circle 1"
                          loading="lazy"
                      />

                      <v-card-title class="fw-700">
                        {{ translation(translationList.road_assistance_title, "Asistenta rutiera") }}
                      </v-card-title>
                      <v-card-text class="fw-500">
                        {{
                          translation(translationList.road_assistance_desc, "Servicii de asistenta rutiera in caz de defectiune sau accident, pentru masini mai noi sau mai vechi.")
                        }}
                      </v-card-text>
                    </v-card>
                  </v-col>
                  <v-col cols="12" md="6" lg="4" xl="4">
                    <v-card
                        color="gray-light"
                        class="mx-auto text-center text-primary"
                        max-width="344"
                        variant="flat"
                    >
                      <NuxtImg
                          src="/images/misc/reason-three.png"
                          srcset="/images/misc/reason-three-small.png 110w, /images/misc/reason-three.png 200w"
                          sizes="(max-width: 600px) 480px, 1024px"
                          style="object-fit: cover; max-width: 180px;"
                          :class="playAnimations ? 'pulse animated' : ''"
                          alt="Circle 1"
                          loading="lazy"
                      />

                      <v-card-title class="fw-700">
                        {{ translation(translationList.diary_title, "Jurnal auto si facturi") }}
                      </v-card-title>
                      <v-card-text class="fw-500">
                        {{
                          translation(translationList.diary_desc, "Poti pastra un jurnal auto pentru masina ta, precum si toate facturile legate de cheltuielile avute.")
                        }}
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </div>
            </v-container>
          </v-container>
        </v-container>
      </section>

      <!-- Xiga Story container -->
      <v-container fluid class="component-container-full mt-10">
        <v-container class="container">
          <v-container class="component-container-1400mw">
            <v-card rounded="xl" min-height="200" class="background-gradient pa-5">
              <v-row class="d-flex align-center justify-center">
                <v-col cols="12" sm="9" class="text-center">
                  <div class="fw-800 fs-30 text-white">
                    {{ translation(translationList.xiga_story_title, "XIGA – Partenerul tău pentru asigurări moderne și accesibile") }}
                  </div>
                </v-col>
                <v-col cols="12" sm="8" class="text-center">
                  <div class="fw-500 fs-20 text-white">
                    <span v-html="translation(translationList.xiga_story_desc, 'La XIGA, credem în puterea parteneriatelor și a tehnologiei pentru a oferi consumatorilor o experiență simplificată și intuitivă în achiziționarea asigurărilor. Colaborăm cu afaceri din diverse industrii, de la comercianți online la magazine fizice, pentru a integra soluții inovatoare și conforme cerințelor de reglementare.')"></span>
                  </div>
                  <br>
                  <div class="fw-500 fs-20 text-white">
                    <span v-html="translation(translationList.xiga_story_desc2, '<strong>Conveniența este prioritatea noastră.</strong> Prin platformele noastre mobile-first, clienții pot achiziționa rapid asigurări precum RCA, asigurări de călătorie sau de locuință, fie online, fie direct din locațiile de proximitate. Procesul este intuitiv, iar tehnologia noastră asigură o experiență fluidă, adaptată nevoilor utilizatorilor moderni.')"></span>
                  </div>
                  <br>
                  <div class="fw-500 fs-20 text-white">
                    <span v-html="translation(translationList.xiga_story_desc3, '<strong>Oportunități de parteneriat.</strong> Pentru antreprenori și afaceri, XIGA oferă un model unic de colaborare, care transformă locațiile de proximitate în centre de acces pentru servicii financiare esențiale. Soluțiile noastre nu doar că sporesc veniturile partenerilor, în calitate de parteneri de promovare, ci și contribuie la consolidarea relației acestora cu comunitățile locale prin servicii utile și necesare.')"></span>
                  </div>
                  <br>
                  <div class="fw-500 fs-20 text-white">
                    {{
                      translation(translationList.xiga_story_desc4, "Indiferent dacă ești consumator sau antreprenor, XIGA face ca accesul la asigurări să fie mai simplu, mai rapid și mai convenabil. Alătură-te rețelei noastre și descoperă beneficiile unei abordări inovatoare și responsabile.")
                    }}
                  </div>
                </v-col>
              </v-row>
            </v-card>
          </v-container>
        </v-container>
      </v-container>

      <!-- FAQ container -->
      <!-- NXS-234 for the moment we hide the FAQ -->
      <v-container v-if="faqCategories && faqCategories.length > 0" fluid class="component-container-full">
        <v-container class="container">
          <v-container class="component-container-800mw" style="min-height: 350px;">
            <div class="d-flex flex-column align-center justify-center">
              <div class="fw-700 fs-30 text-center">
                {{ translation(translationList.faq_title, "Întrebări frecvente") }}
              </div>
            </div>
            <div class="mt-10">
              <v-tabs
                  class="faq-tab"
                  v-model="faqTab"
                  fixed-tabs
                  center-active
                  show-arrows
              >
                <v-tab variant="outlined" class="fw-600 text-dark" selected-class="selected" rounded="xl"
                       v-for="(faqCategory, index) in faqCategories" :key="index">
                  {{ faqCategory.category }}
                </v-tab>
              </v-tabs>

              <v-tabs-window v-model="faqTab">
                <v-tabs-window-item
                    v-for="(faqCategory, index) in faqCategories"
                    :key="index"
                    :value="index"
                >
                  <v-expansion-panels class="my-5 border-solid-contrast">
                    <v-expansion-panel
                        bg-color="gray-light"
                        rounded="xl"
                        v-for="(faqQuestion, qIndex) in filteredQuestions(<number>faqCategory.id)"
                        :key="qIndex"
                    >
                      <template v-slot:title>
                        <div class="fw-700 fs-20">{{ faqQuestion.question }}</div>
                      </template>
                      <template v-slot:text>
                        <div class="fw-500 fs-16 mx-4">
                          {{ faqQuestion.answer }}
                        </div>
                      </template>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-tabs-window-item>
              </v-tabs-window>
            </div>
          </v-container>
        </v-container>
      </v-container>
    </v-container>
  </master-layout>
</template>

<style scoped>
</style>
